import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { MeasurementSilhouette } from "@/components/measurements/MeasurementSilhouette";
import { MeasurementSummaryCard } from "@/components/measurements/MeasurementSummaryCard";
import { MeasurementVersionList } from "@/components/measurements/MeasurementVersionList";
import { toast } from "sonner";
import {
  Ruler,
  Calendar,
  AlertCircle,
  Plus,
  Eye,
} from "lucide-react";

interface MeasurementVersion {
  id: string;
  versionNo: number;
  status: string;
  capturedAt: string;
  capturedBy?: {
    name: string;
    publicId: string;
  };
  bustCm: number;
  waistCm: number;
  hipCm: number;
  shoulderCm: number;
  sleeveLengthCm: number;
  dressLengthCm: number;
  notes?: string;
  appointmentId?: string;
}

interface MeasurementsData {
  hasProfile: boolean;
  profileId?: string;
  activeVersion: MeasurementVersion | null;
  pendingVersions: MeasurementVersion[];
  history: MeasurementVersion[];
}

export default function CustomerMeasurementsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MeasurementsData | null>(null);

  useEffect(() => {
    fetchMeasurements();
  }, []);

  async function fetchMeasurements() {
    try {
      const response = await fetch("/api/customer/measurements");
      if (!response.ok) throw new Error("Failed to fetch measurements");
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error("Failed to load measurements");
    } finally {
      setLoading(false);
    }
  }

  function handleViewVersion(versionId: string) {
    navigate(`/customer/measurements/review/${versionId}`);
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="My Measurements" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  const hasPending = data?.pendingVersions && data.pendingVersions.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Measurements"
        actions={
          <Button onClick={() => navigate("/customer/measurements/book")}>
            <Calendar className="h-4 w-4 mr-2" />
            Book Measurement Visit
          </Button>
        }
      />

      {hasPending && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="py-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    New Measurements Pending Your Review
                  </h3>
                  <p className="text-sm text-slate-600">
                    {data.pendingVersions.length} new measurement
                    {data.pendingVersions.length > 1 ? "s" : ""} submitted. Please review and accept.
                  </p>
                </div>
              </div>
              <Button
                onClick={() =>
                  navigate(
                    `/customer/measurements/review/${data.pendingVersions[0].id}`
                  )
                }
              >
                <Eye className="h-4 w-4 mr-2" />
                Review & Accept
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!data?.hasProfile && !hasPending ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={Ruler}
              title="No Measurements Yet"
              description="Book a measurement visit to get your body measurements taken by our professional staff."
              action={
                <Button onClick={() => navigate("/customer/measurements/book")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Book Measurement Visit
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : data?.activeVersion ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <MeasurementSilhouette
            data={{
              bust: data.activeVersion.bustCm,
              waist: data.activeVersion.waistCm,
              hip: data.activeVersion.hipCm,
              shoulder: data.activeVersion.shoulderCm,
              sleeveLength: data.activeVersion.sleeveLengthCm,
              dressLength: data.activeVersion.dressLengthCm,
              unit: "cm",
            }}
          />

          <div className="space-y-6">
            <MeasurementSummaryCard
              measurements={data.activeVersion}
              versionNo={data.activeVersion.versionNo}
              capturedAt={data.activeVersion.capturedAt}
              status={data.activeVersion.status}
            />

            {data.history.length > 1 && (
              <MeasurementVersionList
                versions={data.history.filter(
                  (v) => v.id !== data.activeVersion?.id
                )}
                onViewVersion={handleViewVersion}
                title="Previous Versions"
              />
            )}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <p>No active measurements found.</p>
              {hasPending && (
                <p className="text-sm mt-2">
                  Accept your pending measurements to see them here.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
